<script lang="ts">
	import { space, spaces } from '../../stores';
	import { switchSpace, createNewSpace, renameCurrentSpace, deleteSpace } from '../../lib/space';
	import { CaretDown as CaretDownIcon, Plus as PlusIcon, Pencil as PencilIcon, Trash as TrashIcon, Check as CheckIcon, X as XIcon } from 'phosphor-svelte';

	let showMenu = false;
	let menuElement: HTMLElement | null = null;
	let renamingSpaceId: string | null = null;
	let renameValue = '';
	let deletingSpaceId: string | null = null;
	let isCreating = false;
	let newSpaceName = '';
	let renameInput: HTMLInputElement;
	let createInput: HTMLInputElement;

	$: currentSpaceName = $space?.name || 'Loading...';
	$: currentSpaceId = $space?.id;

	function toggleMenu() {
		showMenu = !showMenu;
		renamingSpaceId = null;
		deletingSpaceId = null;
		isCreating = false;
	}

	function handleClickOutside(event: MouseEvent) {
		if (!showMenu || !menuElement) return;
		if (menuElement.contains(event.target as Node)) return;

		// Don't close if actively renaming, deleting, or creating (allows drag-select outside menu)
		if (renamingSpaceId || deletingSpaceId || isCreating) return;

		showMenu = false;
	}

	async function handleSwitchSpace(spaceId: string) {
		if (spaceId === currentSpaceId) return;
		showMenu = false;
		await switchSpace(spaceId);
	}

	function startRename(spaceId: string, currentName: string) {
		renameValue = currentName;
		renamingSpaceId = spaceId;
		isCreating = false;
		setTimeout(() => renameInput?.focus(), 0);
	}

	async function confirmRename() {
		if (renameValue.trim() && renamingSpaceId === currentSpaceId) {
			await renameCurrentSpace(renameValue.trim());
		}
		renamingSpaceId = null;
	}

	function cancelRename() {
		renamingSpaceId = null;
		renameValue = '';
	}

	function startCreate() {
		newSpaceName = '';
		isCreating = true;
		renamingSpaceId = null;
		setTimeout(() => createInput?.focus(), 0);
	}

	async function confirmCreate() {
		if (newSpaceName.trim()) {
			showMenu = false;
			isCreating = false;
			await createNewSpace(newSpaceName.trim());
		}
	}

	function cancelCreate() {
		isCreating = false;
		newSpaceName = '';
	}

	function startDelete(spaceId: string) {
		deletingSpaceId = spaceId;
		renamingSpaceId = null;
		isCreating = false;
	}

	async function confirmDelete() {
		if (deletingSpaceId) {
			await deleteSpace(deletingSpaceId);
		}
		deletingSpaceId = null;
	}

	function cancelDelete() {
		deletingSpaceId = null;
	}

	function handleRenameKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			confirmRename();
		} else if (event.key === 'Escape') {
			cancelRename();
		}
	}

	function handleCreateKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			confirmCreate();
		} else if (event.key === 'Escape') {
			cancelCreate();
		}
	}
</script>

<svelte:window on:click={handleClickOutside} />

<div class="space-menu-container" bind:this={menuElement}>
	<button
		class="space-button"
		type="button"
		aria-label="Select space"
		on:click={toggleMenu}
		title="Select space"
	>
		<span class="space-name">{currentSpaceName}</span>
		<CaretDownIcon size={16} />
	</button>

	{#if showMenu}
		<div class="space-menu">
			<div class="menu-section">
				<div class="menu-label">Spaces ({$spaces.length})</div>
				<div class="spaces-list">
					{#each $spaces as spaceInfo}
						<div class="space-item" class:active={spaceInfo.id === currentSpaceId}>
							{#if renamingSpaceId === spaceInfo.id}
								<div class="inline-edit">
									<input
										bind:this={renameInput}
										bind:value={renameValue}
										class="rename-input"
										type="text"
										placeholder="Space name"
										on:keydown={handleRenameKeydown}
										on:click|stopPropagation
									/>
									<button
										class="inline-action-button confirm"
										type="button"
										aria-label="Confirm rename"
										on:click|stopPropagation={confirmRename}
									>
										<CheckIcon size={14} />
									</button>
									<button
										class="inline-action-button cancel"
										type="button"
										aria-label="Cancel rename"
										on:click|stopPropagation={cancelRename}
									>
										<XIcon size={14} />
									</button>
								</div>
							{:else if deletingSpaceId === spaceInfo.id}
								<div class="inline-edit delete-confirm">
									<span class="delete-prompt">Delete "{spaceInfo.name}"?</span>
									<button
										class="inline-action-button confirm"
										type="button"
										aria-label="Confirm delete"
										on:click|stopPropagation={confirmDelete}
									>
										<CheckIcon size={14} />
									</button>
									<button
										class="inline-action-button cancel"
										type="button"
										aria-label="Cancel delete"
										on:click|stopPropagation={cancelDelete}
									>
										<XIcon size={14} />
									</button>
								</div>
							{:else}
								<button
									class="space-item-button"
									type="button"
									on:click={() => handleSwitchSpace(spaceInfo.id)}
								>
									{spaceInfo.name}
								</button>
								{#if spaceInfo.id === currentSpaceId}
									<button
										class="space-action-button"
										type="button"
										aria-label="Rename space"
										on:click|stopPropagation={() => startRename(spaceInfo.id, spaceInfo.name)}
										title="Rename"
									>
										<PencilIcon size={14} />
									</button>
									{#if $spaces.length > 1}
										<button
											class="space-action-button danger"
											type="button"
											aria-label="Delete space"
											on:click|stopPropagation={() => startDelete(spaceInfo.id)}
											title="Delete"
										>
											<TrashIcon size={14} />
										</button>
									{/if}
								{/if}
							{/if}
						</div>
					{/each}
				</div>
			</div>

			<div class="menu-divider"></div>

			{#if isCreating}
				<div class="create-input-container">
					<input
						bind:this={createInput}
						bind:value={newSpaceName}
						class="create-input"
						type="text"
						placeholder="New space name"
						on:keydown={handleCreateKeydown}
					/>
					<button
						class="inline-action-button confirm"
						type="button"
						aria-label="Create space"
						on:click={confirmCreate}
					>
						<CheckIcon size={14} />
					</button>
					<button
						class="inline-action-button cancel"
						type="button"
						aria-label="Cancel"
						on:click={cancelCreate}
					>
						<XIcon size={14} />
					</button>
				</div>
			{:else}
				<button
					class="menu-item-button create-button"
					type="button"
					on:click={startCreate}
				>
					<PlusIcon size={16} />
					<span>New Space</span>
				</button>
			{/if}
		</div>
	{/if}
</div>

<style>
	.space-menu-container {
		position: relative;
	}

	.space-button {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 12px;

		background: transparent;
		border: none;
		border-radius: 8px;
		color: var(--text);
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;

		transition:
			background 0.15s ease,
			transform 0.06s ease;
	}

	.space-button:hover {
		background: var(--hover);
	}

	.space-button:active {
		transform: scale(0.98);
	}

	.space-name {
		max-width: 150px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.space-menu {
		position: absolute;
		top: calc(100% + 8px);
		left: 0;
		min-width: 240px;

		background: var(--panelSolid);
		border: 1px solid var(--border);
		border-radius: 8px;
		box-shadow: var(--shadowLight);

		padding: 8px;
		z-index: 10000;
	}

	.menu-section {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.menu-label {
		font-size: 11px;
		font-weight: 600;
		color: var(--textMuted);
		padding: 6px 12px 4px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.spaces-list {
		display: flex;
		flex-direction: column;
		gap: 4px;
		max-height: 300px;
		overflow-y: auto;
	}

	.space-item {
		display: flex;
		align-items: center;
		gap: 4px;
		border-radius: 6px;
		transition: background 0.15s ease;
	}

	.space-item:hover {
		background: var(--hover);
	}

	.space-item.active {
		background: var(--hover);
	}

	.space-item-button {
		flex: 1;
		padding: 10px 12px;

		background: transparent;
		border: none;
		border-radius: 6px;
		color: var(--text);
		cursor: pointer;
		font-size: 14px;
		text-align: left;

		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.space-action-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		margin-right: 4px;

		background: transparent;
		border: none;
		border-radius: 4px;
		color: var(--textMuted);
		cursor: pointer;

		transition: background 0.15s ease, color 0.15s ease;
	}

	.space-action-button:hover {
		background: var(--hover);
	}

	.space-action-button.danger:hover {
		background: rgba(220, 38, 38, 0.1);
		color: #DC2626;
	}

	.menu-divider {
		height: 1px;
		background: var(--border);
		margin: 8px 0;
	}

	.menu-item-button {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		padding: 10px 12px;

		background: transparent;
		border: none;
		border-radius: 6px;
		color: var(--text);
		cursor: pointer;
		font-size: 14px;
		text-align: left;

		transition: background 0.15s ease;
	}

	.menu-item-button:hover {
		background: var(--hover);
	}

	.create-button {
		color: var(--userAccent);
	}

	.inline-edit {
		display: flex;
		align-items: center;
		gap: 4px;
		flex: 1;
		padding: 4px;
	}

	.delete-confirm {
		padding: 6px 8px;
	}

	.delete-prompt {
		flex: 1;
		font-size: 13px;
		color: var(--text);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.rename-input,
	.create-input {
		flex: 1;
		padding: 6px 10px;

		background: var(--inputBg);
		border: 1px solid var(--border);
		border-radius: 4px;
		color: var(--text);
		font-size: 14px;
		outline: none;

		transition: border-color 0.15s ease;
	}

	.rename-input:focus,
	.create-input:focus {
		border-color: var(--userAccent);
	}

	.create-input-container {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 8px;
	}

	.inline-action-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;

		background: transparent;
		border: none;
		border-radius: 4px;
		cursor: pointer;

		transition: background 0.15s ease;
	}

	.inline-action-button.confirm {
		color: #16A34A;
	}

	.inline-action-button.confirm:hover {
		background: rgba(22, 163, 74, 0.1);
	}

	.inline-action-button.cancel {
		color: var(--textMuted);
	}

	.inline-action-button.cancel:hover {
		background: var(--hover);
	}
</style>
